import { useEffect, useRef, useState } from 'react';
import { Navigate, NavigationType, Route, Routes, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { App as AppNativo } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useSesion } from './servicios/sesion.js';
import { Disposicion } from './componentes/disposicion.js';
import {
  ActualizacionRequerida,
  AvisoActualizacionOpcional,
  Cargando,
  EnMantenimiento,
  cerrarDialogoSuperior,
} from './componentes/piezas.js';
import { Acceso } from './paginas/acceso.js';
import { SolicitudPublica } from './paginas/solicitud-publica.js';
import { ListaDispositivos } from './paginas/dispositivos.js';
import { MisTablets } from './paginas/mis-tablets.js';
import { MovilDispositivo } from './paginas/movil-dispositivo.js';
import { Dashboard } from './paginas/dashboard.js';
import { CalendarioOrdenes, DetalleOrden, EditarOrden, GanttOrdenes, ListaOrdenes, NuevaOrden } from './paginas/ordenes.js';
import { AdministrarCatalogos } from './paginas/catalogos.js';
import { ConflictosSede, DetalleSolicitud, ListaSolicitudes, NuevaSolicitud } from './paginas/solicitudes.js';
import { ListasMateriales } from './paginas/listas-materiales.js';
import { DetalleMaterialCatalogo } from './paginas/material-catalogo-detalle.js';
import { ListaActivos, ListaTiposFalla, ListaUbicaciones, ListaUsuarios } from './paginas/maestros.js';
import { DetalleActivo, DetalleUbicacion } from './paginas/detalle-maestros.js';
import { AlertasActivos } from './paginas/alertas-activos.js';
import { DetalleInventario, ListaInventarios } from './paginas/inventarios.js';
import { Licencia } from './paginas/licencia.js';
import { Gestion } from './paginas/gestion.js';
import { ActividadReciente, EscanearQr, Notificaciones } from './paginas/varios.js';
import { BibliotecaProcedimientos, EditorProcedimiento, HistorialEjecuciones } from './paginas/procedimientos.js';
import { EjecucionChecklist } from './paginas/ejecucion.js';
import { DetalleParte, ListaPartes, Reposicion } from './paginas/inventario.js';
import { Medidores } from './paginas/medidores.js';
import { Personal } from './paginas/personal.js';
import { DetallePersona } from './paginas/detalle-persona.js';
import { Reportes } from './paginas/reportes.js';
import { ProveedorSesionOperador } from './servicios/sesion-operador.js';
import { ConsolaPlataforma } from './paginas/plataforma/consola.js';
import { RutaNoDisponible } from './componentes/ruta-no-disponible.js';

/**
 * Dos aplicaciones bajo el mismo dominio.
 *
 * `/plataforma` es la consola de operadores y se resuelve antes que nada: tiene
 * su propio proveedor de sesión, de modo que llegar allí sin una sesión de
 * organización es normal, no un error. El resto es el producto de siempre.
 */
export function App(): JSX.Element {
  useBotonAtrasNativo();

  return (
    <Routes>
      <Route
        path="/plataforma/*"
        element={
          <ProveedorSesionOperador>
            <ConsolaPlataforma />
          </ProveedorSesionOperador>
        }
      />
      {/* Solicitud desde el QR de un equipo: sin sesión, fuera de la compuerta de login. */}
      <Route path="/s" element={<SolicitudPublica />} />
      <Route path="/s/:codigo" element={<SolicitudPublica />} />
      {/* Reporte desde una tablet de kiosko emparejada a una cuenta de sede: también fuera del login. */}
      <Route path="/movil" element={<MovilDispositivo />} />
      <Route path="*" element={<AplicacionTenant />} />
    </Routes>
  );
}

/**
 * En Android, el botón físico/gesto de retroceso, por defecto, cierra la app
 * apenas no hay historial de navegador nativo (el WebView de una SPA nunca lo
 * tiene). Lo redirigimos al historial de React Router.
 *
 * `window.history.state.idx` no sirve aquí: en pruebas en dispositivo real
 * queda siempre en 0 (el WebView de Capacitor no lo persiste igual que un
 * navegador de escritorio), así que llevamos la cuenta nosotros mismos
 * contando PUSH/POP de `useNavigationType`.
 */
function useBotonAtrasNativo(): void {
  const navegar = useNavigate();
  const ubicacion = useLocation();
  const tipoNavegacion = useNavigationType();
  const profundidad = useRef(0);

  useEffect(() => {
    if (tipoNavegacion === NavigationType.Push) {
      profundidad.current += 1;
    } else if (tipoNavegacion === NavigationType.Pop) {
      profundidad.current = Math.max(0, profundidad.current - 1);
    }
  }, [ubicacion, tipoNavegacion]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    const suscripcion = AppNativo.addListener('backButton', () => {
      // Un diálogo abierto (alta/edición, confirmar, el visor de fotos) se
      // cierra primero: es lo que espera cualquiera que use el back nativo de
      // Android, y sin esto un formulario sin guardar en la primera pantalla
      // de la sesión (sin historial al que volver) mandaba la app a segundo
      // plano en vez de cerrar el modal.
      if (cerrarDialogoSuperior()) return;
      if (profundidad.current > 0) {
        navegar(-1);
      } else {
        void AppNativo.minimizeApp();
      }
    });

    return () => {
      void suscripcion.then((s) => s.remove());
    };
  }, [navegar]);
}

function AplicacionTenant(): JSX.Element {
  const {
    usuario,
    cargando,
    verificandoVersion,
    mantenimiento,
    mensajeMantenimiento,
    actualizacionForzada,
    actualizacionOpcional,
    descartarActualizacionOpcional,
    reverificarVersion,
    urlTienda,
    urlDescarga,
    puede,
  } = useSesion();
  const [reintentandoMantenimiento, setReintentandoMantenimiento] = useState(false);

  const reintentar = (): void => {
    setReintentandoMantenimiento(true);
    void reverificarVersion().finally(() => setReintentandoMantenimiento(false));
  };

  // Antes que nada: mantenimiento y versión vieja bloquean incluso el login, así que se revisan
  // antes de que `cargando`/`usuario` tengan oportunidad de decidir nada. Mantenimiento primero
  // porque es independiente de la versión instalada — no tiene sentido mandar a alguien a
  // actualizar cuando el problema real es que el backend está caído a propósito.
  if (verificandoVersion) return <Cargando texto="Verificando versión…" />;
  if (mantenimiento) {
    return (
      <EnMantenimiento
        mensaje={mensajeMantenimiento}
        onReintentar={reintentar}
        reintentando={reintentandoMantenimiento}
      />
    );
  }
  if (actualizacionForzada) return <ActualizacionRequerida urlTienda={urlTienda} urlDescarga={urlDescarga} />;

  if (cargando) return <Cargando texto="Restaurando sesión…" />;

  // El aviso opcional flota sobre cualquier pantalla —login incluido—, sin bloquear nada debajo.
  const avisoOpcional = actualizacionOpcional ? (
    <AvisoActualizacionOpcional
      urlTienda={urlTienda}
      urlDescarga={urlDescarga}
      onDescartar={descartarActualizacionOpcional}
    />
  ) : null;

  if (!usuario) {
    return (
      <>
        <Acceso />
        {avisoOpcional}
      </>
    );
  }

  // El Dashboard es la única pantalla que todos los roles pueden abrir: decide
  // internamente qué mostrar según el tipo de acceso (§12.5).
  const inicio = '/dashboard';

  return (
    <>
      <Routes>
        <Route element={<Disposicion />}>
          <Route path="/" element={<Navigate to={inicio} replace />} />
          <Route path="/dashboard" element={<Dashboard />} />

          {puede('ot:leer') ? (
            <>
              <Route path="/ordenes" element={<ListaOrdenes />} />
              {/* Antes que «/ordenes/:id»: si no, «nueva»/«calendario»/«gantt» se leen como un id. */}
              {puede('ot:crear') ? <Route path="/ordenes/nueva" element={<NuevaOrden />} /> : null}
              <Route path="/ordenes/calendario" element={<CalendarioOrdenes />} />
              <Route path="/ordenes/gantt" element={<GanttOrdenes />} />
              <Route path="/ordenes/:id" element={<DetalleOrden />} />
              {puede('ot:editar') ? <Route path="/ordenes/:id/editar" element={<EditarOrden />} /> : null}
            </>
          ) : null}

          <Route path="/solicitudes" element={<ListaSolicitudes />} />
          <Route path="/mis-tablets" element={<MisTablets />} />
          <Route path="/solicitudes/nueva" element={<NuevaSolicitud />} />
          {puede('solicitud:leer_todas') ? <Route path="/solicitudes/conflictos" element={<ConflictosSede />} /> : null}
          <Route path="/solicitudes/:id" element={<DetalleSolicitud />} />
          <Route path="/notificaciones" element={<Notificaciones />} />

          {puede('procedimiento:leer') ? (
            <>
              <Route path="/procedimientos" element={<BibliotecaProcedimientos />} />
              {/* Antes que «/procedimientos/:id»: si no, «historial» se lee como un id. */}
              <Route path="/procedimientos/historial" element={<HistorialEjecuciones />} />
              <Route path="/procedimientos/:id" element={<EditorProcedimiento />} />
            </>
          ) : null}
          {puede('procedimiento:ejecutar') ? <Route path="/ejecuciones/:id" element={<EjecucionChecklist />} /> : null}

          {puede('parte:leer') ? (
            <>
              <Route path="/partes" element={<ListaPartes />} />
              <Route path="/partes/:id" element={<DetalleParte />} />
              <Route path="/reposicion" element={<Reposicion />} />
            </>
          ) : null}

          {puede('medidor:leer') ? <Route path="/medidores" element={<Medidores />} /> : null}
          {puede('sujeto_evaluable:leer') ? <Route path="/personal" element={<Personal />} /> : null}
          {puede('sujeto_evaluable:leer') ? <Route path="/personal/:id" element={<DetallePersona />} /> : null}
          {puede('activo:leer') ? <Route path="/activos" element={<ListaActivos />} /> : null}
          {/* Antes que «/activos/:id»: si no, «alertas» se lee como un id. Incluye
              observaciones de ubicaciones, por eso también se abre con ese permiso. */}
          {puede('activo:leer') || puede('ubicacion:leer') ? (
            <Route path="/activos/alertas" element={<AlertasActivos />} />
          ) : null}
          {puede('activo:leer') ? <Route path="/activos/inventarios" element={<ListaInventarios />} /> : null}
          {puede('activo:leer') ? <Route path="/activos/inventarios/:id" element={<DetalleInventario />} /> : null}
          {puede('activo:leer') ? <Route path="/activos/:id" element={<DetalleActivo />} /> : null}
          {puede('ubicacion:leer') ? <Route path="/ubicaciones" element={<ListaUbicaciones />} /> : null}
          {puede('ubicacion:leer') ? <Route path="/ubicaciones/:id" element={<DetalleUbicacion />} /> : null}
          {puede('tipo_falla:leer') ? <Route path="/tipos-falla" element={<ListaTiposFalla />} /> : null}
          {puede('usuario:leer') ? <Route path="/usuarios" element={<ListaUsuarios />} /> : null}
          {puede('usuario:administrar') ? <Route path="/dispositivos" element={<ListaDispositivos />} /> : null}
          {puede('catalogo:administrar') ? <Route path="/catalogos" element={<AdministrarCatalogos />} /> : null}
          {puede('lista_materiales:editar') ? <Route path="/listas-materiales" element={<ListasMateriales />} /> : null}
          {puede('lista_materiales:editar') ? (
            <Route path="/listas-materiales/materiales/:id" element={<DetalleMaterialCatalogo />} />
          ) : null}
          {/* Le sirve tanto a quien gestiona activos como a un Solicitante
              reportando desde el celular — mismo criterio que la entrada de
              menú en disposicion.tsx. */}
          {puede('activo:leer') || puede('solicitud:crear') ? <Route path="/qr" element={<EscanearQr />} /> : null}
          {puede('reporte:leer') ? <Route path="/reportes" element={<Reportes />} /> : null}
          {puede('auditoria:leer') ? <Route path="/actividad" element={<ActividadReciente />} /> : null}
          {puede('licencia:leer') ? <Route path="/licencia" element={<Licencia />} /> : null}
          {puede('app_version:gestionar') ? <Route path="/gestion" element={<Gestion />} /> : null}

          <Route path="*" element={<RutaNoDisponible destino={inicio} />} />
        </Route>
      </Routes>
      {avisoOpcional}
    </>
  );
}
